import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { severityStyle } from "../lib/warnings";
import {
  X, Car, AlertTriangle, CheckCircle2, ArrowRightLeft, Send, Sparkles, ListChecks,
} from "lucide-react";
import { toast } from "sonner";

const PILL_BY_AVAIL = {
  available:   { c: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  warning:     { c: "bg-amber-50 text-amber-800 border-amber-200",       icon: AlertTriangle },
  conflict:    { c: "bg-red-50 text-red-800 border-red-200",             icon: AlertTriangle },
  unavailable: { c: "bg-slate-100 text-slate-700 border-slate-200",      icon: AlertTriangle },
};

const REASON_TEMPLATES = [
  "Vehicle in maintenance",
  "Mechanical issue found",
  "Customer requested change",
  "Vehicle unavailable",
  "Staff scheduling correction",
  "Better replacement available",
  "Owner approved replacement",
];

/**
 * Reassign / replacement vehicle modal.
 *
 * Props:
 *  - bookingId (string, required)
 *  - open (bool)
 *  - onClose ()
 *  - onDone (result): called after a successful reassignment
 *  - preloaded ({booking, candidates}, optional): sandboxed-demo hook,
 *    same convention as CustomerConfirmation.jsx/RentalProfile.jsx. When
 *    supplied, the real GET .../replacement-candidates fetch is skipped
 *    and this data is used instead.
 *  - onSubmit ((payload) => result, optional), when supplied together
 *    with `preloaded`, replaces the real POST .../reassign-vehicle call;
 *    called with the same payload shape the real endpoint expects.
 *  - onNoSuitableReplacement (optional): demo/supporting-flow escape hatch
 *    shown inside the chooser. Production callers omit it and retain the
 *    existing modal behavior.
 *
 * Usage:
 *   <ReassignVehicleModal
 *     bookingId={bookingId}
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     onDone={() => refresh()}
 *   />
 */
export default function ReassignVehicleModal({
  bookingId,
  open,
  onClose,
  onDone,
  preloaded = null,
  onSubmit = null,
  onNoSuitableReplacement = null,
}) {
  const [data, setData] = useState(preloaded);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null);
  const [reason, setReason] = useState("");
  const [overrideNote, setOverrideNote] = useState("");
  const [notify, setNotify] = useState(false);
  const [customerMessage, setCustomerMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // 'choose' → pick smart vs manual first
  // 'select' → manual candidate list
  // 'confirm' → review + submit (smart lands here directly)
  const [step, setStep] = useState("choose");
  const [selectionSource, setSelectionSource] = useState(null); // "smart" | "manual"

  const load = async () => {
    if (preloaded) { setData(preloaded); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/bookings/${bookingId}/replacement-candidates`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (!open) return;
    setPicked(null);
    setReason("");
    setOverrideNote("");
    setNotify(false);
    setCustomerMessage("");
    setStep("choose");
    setSelectionSource(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bookingId, preloaded]);

  if (!open) return null;

  const submit = async () => {
    if (!picked) return;
    setSubmitting(true);
    try {
      const payload = {
        new_item_id: picked.id,
        reason: reason.trim() || null,
        notify_customer: notify,
        selection_source: selectionSource || "manual",
      };
      if (overrideNote.trim()) payload.override_note = overrideNote.trim();
      if (notify && customerMessage.trim()) payload.customer_message = customerMessage.trim();
      const res = onSubmit
        ? await onSubmit(payload)
        : (await api.post(`/bookings/${bookingId}/reassign-vehicle`, payload)).data;
      toast.success(`Booking reassigned to ${res.new_vehicle?.name || "new vehicle"}`);
      onDone?.(res);
      onClose?.();
    } catch (e) {
      // If override required, surface a clear hint
      const status = e?.response?.status;
      if (status === 409) {
        toast.error("Override note required: type a reason in the override field and try again.");
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const booking = data?.booking;
  const candidates = data?.candidates || [];
  const needsOverride = picked && (picked.availability === "conflict"
                                   || picked.availability === "unavailable"
                                   || (picked.vehicle_warnings || []).some((w) => w.severity === "critical"));
  // Smart Suggest picks the top scored candidate that ConfirmPH considers
  // safe.  It's only offered when at least one such vehicle exists; if the
  // whole fleet is unavailable/conflicting we send staff straight to manual.
  const smartPick = candidates.find(
    (c) => c.availability === "available" && c.category_match
  ) || candidates.find((c) => c.availability === "available") || null;

  const chooseSmart = () => {
    if (!smartPick) return;
    setSelectionSource("smart");
    setPicked(smartPick);
    setStep("confirm");
  };
  const chooseManual = () => {
    setSelectionSource("manual");
    setPicked(null);
    setStep("select");
  };

  return (
    <div
      data-testid="reassign-modal"
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-6"
    >
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold inline-flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5 text-[#1B4332]" />
            {step === "confirm" ? "Confirm replacement"
              : step === "choose" ? "Find replacement vehicle"
              : "Choose replacement manually"}
          </h2>
          <button
            data-testid="reassign-close"
            onClick={onClose}
            className="p-2 rounded-md hover:bg-slate-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading && <div className="text-sm text-slate-500" data-testid="reassign-loading">Loading candidates…</div>}

          {!loading && booking && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
              <div className="text-xs uppercase tracking-wider text-slate-500">Booking</div>
              <div className="font-medium text-slate-900">
                #{booking.booking_reference} · {booking.customer_name}
              </div>
              <div className="text-slate-600 text-xs mt-0.5">
                Current: <b>{booking.current_vehicle?.name || "-"}</b>
                {booking.current_vehicle?.plate_number && ` (${booking.current_vehicle.plate_number})`}
                · {new Date(booking.start_datetime).toLocaleString()} → {new Date(booking.end_datetime).toLocaleString()}
              </div>
            </div>
          )}

          {step === "choose" && !loading && (
            <div className="space-y-3" data-testid="reassign-choose">
              <p className="text-sm text-slate-600">
                How would you like to choose the replacement vehicle for this booking?
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <button type="button" onClick={chooseSmart} disabled={!smartPick}
                        data-testid="reassign-choose-smart"
                        className={`text-left rounded-xl border p-4 transition ${
                          smartPick ? "border-emerald-300 bg-emerald-50/60 hover:bg-emerald-50"
                                    : "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed"
                        }`}>
                  <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-emerald-800">
                    <Sparkles className="w-3 h-3" /> Recommended
                  </div>
                  <div className="mt-1 font-display font-semibold text-slate-900">Suggested replacement</div>
                  {smartPick ? (
                    <>
                      <div className="mt-1 text-sm text-slate-700">
                        Use <strong>{smartPick.name}</strong>
                        {smartPick.plate_number ? ` (${smartPick.plate_number})` : ""}
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        Available for the full rental period · {smartPick.category_match ? "same vehicle category" : "closest match in your fleet"} · no maintenance conflict
                      </div>
                    </>
                  ) : (
                    <div className="mt-1 text-xs text-slate-500">
                      No suitable replacement found in your fleet right now: choose manually and add an override note if needed.
                    </div>
                  )}
                </button>

                <button type="button" onClick={chooseManual}
                        data-testid="reassign-choose-manual"
                        className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:bg-slate-50 transition">
                  <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
                    <ListChecks className="w-3 h-3" /> Full control
                  </div>
                  <div className="mt-1 font-display font-semibold text-slate-900">Choose Manually</div>
                  <div className="mt-1 text-sm text-slate-700">
                    Browse every eligible vehicle and pick yourself.
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    {candidates.length} candidate{candidates.length === 1 ? "" : "s"} in fleet · availability, conflicts and warnings shown per vehicle.
                  </div>
                </button>
              </div>
              <p className="text-[11px] text-slate-500">
                ConfirmPH still runs its safety checks either way: a manually picked vehicle with conflicts will require an override note before you can confirm.
              </p>
            </div>
          )}

          {step === "select" && !loading && (
            <>
              {candidates.length === 0 ? (
                <div className="text-center p-6 rounded-lg border border-dashed border-slate-300 text-slate-500 text-sm" data-testid="reassign-empty">
                  No other vehicles in your fleet. Add another vehicle first.
                </div>
              ) : (
                <ul className="space-y-2" data-testid="reassign-candidates">
                  {candidates.map((c) => {
                    const pillCfg = PILL_BY_AVAIL[c.availability] || PILL_BY_AVAIL.warning;
                    const PillIcon = pillCfg.icon;
                    const isPicked = picked?.id === c.id;
                    return (
                      <li
                        key={c.id}
                        data-testid={`reassign-candidate-${c.id}`}
                        className={`rounded-lg border p-3 cursor-pointer transition ${
                          isPicked ? "border-[#1B4332] ring-1 ring-[#1B4332] bg-emerald-50/40" : "border-slate-200 hover:bg-slate-50"
                        }`}
                        onClick={() => setPicked(c)}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-lg bg-slate-100 grid place-items-center flex-shrink-0">
                            <Car className="w-4 h-4 text-slate-700" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-medium text-slate-900">{c.name}</span>
                              {c.plate_number && <span className="text-xs text-slate-500 font-mono">{c.plate_number}</span>}
                              <span className={`text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${pillCfg.c}`} data-testid={`reassign-pill-${c.id}`}>
                                <PillIcon className="w-3 h-3" /> {c.availability_label}
                              </span>
                              {c.category_match && (
                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">Same category</span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              {[c.category, c.transmission, c.seats ? `${c.seats} seats` : null]
                                .filter(Boolean).join(" · ")} · ₱{(c.price_per_day || 0).toLocaleString()}/day
                            </div>
                            {(c.vehicle_warnings || []).length > 0 && (
                              <ul className="mt-2 text-xs space-y-0.5">
                                {c.vehicle_warnings.map((w, i) => {
                                  const s = severityStyle(w.severity);
                                  return (
                                    <li key={i} className={`inline-flex items-center gap-1 ${s.title}`}>
                                      <AlertTriangle className="w-3 h-3" /> {w.message}
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                            {c.conflicts?.has_critical && (
                              <div className="text-xs text-red-700 mt-1 inline-flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" /> Already booked during these dates.
                              </div>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}

          {step === "confirm" && picked && (
            <div className="space-y-4" data-testid="reassign-confirm">
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-wider text-slate-500">From</div>
                    <div className="font-medium">{booking?.current_vehicle?.name || "-"}</div>
                  </div>
                  <ArrowRightLeft className="w-4 h-4 text-slate-400" />
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-wider text-slate-500">To</div>
                    <div className="font-medium text-[#1B4332] inline-flex items-center gap-1.5">
                      {picked.name}
                      <span data-testid="reassign-source-chip"
                            className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold ${
                              selectionSource === "smart" ? "bg-emerald-100 text-emerald-800"
                                                          : "bg-slate-200 text-slate-700"
                            }`}>
                        {selectionSource === "smart" ? "Smart" : "Manual"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Reason (internal)</span>
                <div className="flex flex-wrap gap-1 mt-1.5 mb-1.5" data-testid="reassign-reason-templates">
                  {REASON_TEMPLATES.map((tpl) => (
                    <button
                      key={tpl}
                      type="button"
                      onClick={() => setReason(tpl)}
                      data-testid={`reassign-template-${tpl.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`text-[11px] px-2 py-0.5 rounded-full border ${
                        reason === tpl
                          ? "bg-[#1B4332] text-white border-[#1B4332]"
                          : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                      }`}
                    >
                      {tpl}
                    </button>
                  ))}
                </div>
                <textarea
                  data-testid="reassign-reason"
                  rows={2}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Original vehicle in maintenance; customer agreed."
                  className="w-full rounded-lg border border-slate-300 p-2 text-sm"
                />
              </label>

              {needsOverride && (
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-red-700">Override note (required)</span>
                  <textarea
                    data-testid="reassign-override"
                    rows={2}
                    value={overrideNote}
                    onChange={(e) => setOverrideNote(e.target.value)}
                    placeholder="The replacement has warnings: note why you're proceeding anyway."
                    className="mt-1.5 w-full rounded-lg border border-red-300 p-2 text-sm"
                  />
                </label>
              )}

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  data-testid="reassign-notify-toggle"
                  checked={notify}
                  onChange={(e) => setNotify(e.target.checked)}
                />
                <span>Notify the customer about the change (sends a re-confirmation link)</span>
              </label>

              {notify && (
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Customer message (optional)</span>
                  <textarea
                    data-testid="reassign-customer-message"
                    rows={3}
                    value={customerMessage}
                    onChange={(e) => setCustomerMessage(e.target.value)}
                    placeholder="Leave blank to send a safe default. Avoid mentioning incidents or internal details."
                    className="mt-1.5 w-full rounded-lg border border-slate-300 p-2 text-sm"
                  />
                  <div className="text-[11px] text-slate-500 mt-1">
                    We never expose internal notes or incident details to the customer.
                  </div>
                </label>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2 flex-wrap">
          {step === "choose" && (
            <>
              {onNoSuitableReplacement && (
                <button
                  type="button"
                  onClick={onNoSuitableReplacement}
                  data-testid="reassign-no-suitable-replacement"
                  className="mr-auto px-4 py-2 rounded-lg border border-rose-200 bg-white text-rose-700 hover:bg-rose-50 text-sm font-medium"
                >
                  No suitable replacement available
                </button>
              )}
              <button onClick={onClose} data-testid="reassign-cancel"
                      className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm">
                Cancel
              </button>
            </>
          )}
          {step === "select" && (
            <>
              <button
                onClick={() => setStep("choose")}
                data-testid="reassign-back-to-choose"
                className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm"
              >
                Back
              </button>
              <button
                onClick={() => setStep("confirm")}
                disabled={!picked}
                data-testid="reassign-continue"
                className="px-4 py-2 rounded-lg bg-[#1B4332] text-white text-sm hover:opacity-90 disabled:opacity-50"
              >
                Continue
              </button>
            </>
          )}
          {step === "confirm" && (
            <>
              <button
                onClick={() => setStep(selectionSource === "smart" ? "choose" : "select")}
                data-testid="reassign-back"
                className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm"
              >
                Back
              </button>
              <button
                onClick={submit}
                disabled={submitting || (needsOverride && !overrideNote.trim())}
                data-testid="reassign-submit"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1B4332] text-white text-sm hover:opacity-90 disabled:opacity-50"
              >
                {notify ? <Send className="w-4 h-4" /> : <ArrowRightLeft className="w-4 h-4" />}
                {submitting ? "Saving…" : notify ? "Confirm & notify" : "Confirm replacement"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
